import { resolveBindingValue } from './TemplateBindings';
import { getCanvasMargins, getRepeatCardLayout } from './TemplateLayout';

const getTextValue = (element, bindingData) => {
  const boundValue = resolveBindingValue(bindingData, element.fieldPath);

  if (boundValue !== null && boundValue !== undefined && boundValue !== '') {
    return `${boundValue}`;
  }

  return element.content || '';
};

const getTableRows = (element, bindingData) => {
  const boundRows = resolveBindingValue(bindingData, element.fieldPath);

  return Array.isArray(boundRows) ? boundRows : element.tableData || [];
};

const createPdfElement = (element, bindingData, offset = { x: 0, y: 0 }) => {
  const absolutePosition = { x: offset.x + element.x, y: offset.y + element.y };

  if (element.type === 'text') {
    return {
      absolutePosition,
      alignment: element.styles?.textAlign || 'left',
      bold: element.styles?.fontWeight === 'bold',
      color: element.styles?.color || '#1f2937',
      fontSize: element.styles?.fontSize || 14,
      height: element.height,
      text: getTextValue(element, bindingData),
      width: element.width,
    };
  }

  if (element.type === 'image') {
    if (!element.imageUrl) {
      return null;
    }

    return {
      absolutePosition,
      fit: [element.width, element.height],
      image: element.imageUrl,
    };
  }

  if (element.type === 'line') {
    const isVertical = element.orientation === 'vertical';

    return {
      absolutePosition,
      canvas: [
        {
          lineColor: element.styles?.stroke || '#2b6cb0',
          lineWidth: element.styles?.strokeWidth || 1,
          type: 'line',
          x1: isVertical ? element.width / 2 : 0,
          x2: isVertical ? element.width / 2 : element.width,
          y1: isVertical ? 0 : element.height / 2,
          y2: isVertical ? element.height : element.height / 2,
        },
      ],
    };
  }

  if (element.type === 'table') {
    const columns = element.columns || [];
    const rows = getTableRows(element, bindingData);

    if (columns.length === 0) {
      return null;
    }

    const body = [];

    if (element.showHeaders !== false) {
      body.push(
        columns.map((column) => {
          return { bold: true, text: column.label || column.key || '' };
        }),
      );
    }

    rows.forEach((row) => {
      body.push(
        columns.map((column) => {
          const value = column.fieldPath ? resolveBindingValue(row, column.fieldPath) : row[column.key];

          return { text: value == null ? '' : `${value}` };
        }),
      );
    });

    return {
      absolutePosition,
      fontSize: 9,
      layout: 'lightHorizontalLines',
      table: {
        body,
        headerRows: element.showHeaders === false ? 0 : 1,
        widths: columns.map((column) => {
          return column.width || element.width / Math.max(columns.length, 1);
        }),
      },
    };
  }

  return {
    absolutePosition,
    canvas: [
      {
        ...(element.styles?.fill && element.styles.fill !== 'transparent' ? { color: element.styles.fill } : {}),
        lineColor: element.styles?.stroke || '#2b6cb0',
        lineWidth: element.styles?.strokeWidth || 1,
        type: 'rect',
        w: element.width,
        h: element.height,
        x: 0,
        y: 0,
      },
    ],
  };
};

const renderElements = (elements, bindingData, offset) => {
  return elements.map((element) => {
    return createPdfElement(element, bindingData, offset);
  }).filter(Boolean);
};

const chunkRecords = (records, chunkSize) => {
  const pages = [];

  records.forEach((record, index) => {
    const pageIndex = Math.floor(index / chunkSize);

    if (!pages[pageIndex]) {
      pages[pageIndex] = [];
    }

    pages[pageIndex].push(record);
  });

  return pages.length > 0 ? pages : [[]];
};

const createRepeatPages = (template, records) => {
  const cardLayout = getRepeatCardLayout(template);
  const recordsPerPage = cardLayout.columns * cardLayout.rows;
  const cardPages = chunkRecords(records, recordsPerPage);

  return cardPages.map((pageRecords) => {
    return pageRecords.reduce((pageElements, bindingData, recordIndex) => {
      const columnIndex = recordIndex % cardLayout.columns;
      const rowIndex = Math.floor(recordIndex / cardLayout.columns);
      const offset = {
        x: cardLayout.x + columnIndex * (cardLayout.cardWidth + cardLayout.gap),
        y: cardLayout.y + rowIndex * (cardLayout.cardHeight + cardLayout.gap),
      };

      return [...pageElements, ...renderElements(template.regions.dataContent.elements, bindingData, offset)];
    }, []);
  });
};

export const buildTemplatePdfDefinition = ({ bindingData, records, template }) => {
  const { canvas, regions } = template;
  const margins = getCanvasMargins(template);
  const isRepeating = regions.dataContent.mode === 'repeat';
  const pages = isRepeating
    ? createRepeatPages(template, records)
    : [renderElements(regions.dataContent.elements, bindingData, { x: margins.left, y: margins.top })];
  const content = pages.flatMap((pageElements, index) => {
    const pageAnchor = { fontSize: 1, text: ' ' };

    return index < pages.length - 1 ? [...pageElements, { ...pageAnchor, pageBreak: 'after' }] : [...pageElements, pageAnchor];
  });

  return {
    content,
    defaultStyle: { fontSize: 12 },
    footer: () => {
      return regions.footer.enabled === false
        ? []
        : renderElements(regions.footer.elements, bindingData, { x: margins.left, y: -margins.bottom });
    },
    header: () => {
      return regions.header.enabled === false
        ? []
        : renderElements(regions.header.elements, bindingData, { x: margins.left, y: margins.top });
    },
    info: { title: template.name || 'RezRATE Print Materials' },
    pageMargins: [0, 0, 0, 0],
    pageSize: { height: canvas.height, width: canvas.width },
  };
};
